import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Topbar } from "../components/Topbar";
import { KpiCard } from "../components/KpiCard";
import { Panel } from "../components/Panel";
import { LoadingState, ErrorState } from "../components/States";
import { useDashboard } from "../hooks/useDashboard";
import { formatCurrency, formatHours, formatPct } from "../lib/dates";
import { ChevronRight, ChevronDown, AlertTriangle, ExternalLink } from "lucide-react";
import { cn } from "../lib/utils";

// Flatten the tree iteratively into a list of rows ({type, depth, node, parentKey})
// "expanded" is a Set of plan ids whose children should be visible.
const flattenTree = (roots, expanded) => {
  const out = [];
  const walk = (node, depth) => {
    out.push({ kind: "plan", node, depth });
    if (!expanded.has(node.id)) return;
    for (const child of node.children || []) walk(child, depth + 1);
    for (const acc of node.accounts || []) {
      out.push({ kind: "account", node: acc, depth: depth + 1 });
    }
  };
  for (const r of roots) walk(r, 0);
  return out;
};

export default function PnlAnalitico() {
  const { data, loading, error } = useDashboard("pnl_analytic");
  const navigate = useNavigate();

  // Default expansion: open the first level (roots are auto-included)
  const initialExpanded = useMemo(() => {
    const set = new Set();
    if (data?.tree?.length) {
      data.tree.forEach((r) => set.add(r.id));
    }
    return set;
  }, [data]);
  const [expanded, setExpanded] = useState(initialExpanded);

  // Re-init when data changes
  const dataKey = data?.summary?.income;
  useMemo(() => {
    setExpanded(initialExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = useMemo(
    () => (data?.tree ? flattenTree(data.tree, expanded) : []),
    [data, expanded]
  );

  return (
    <>
      <Topbar title="P&G Analítico" subtitle="Rentabilidad analítica jerárquica: empresa → departamento → cuenta" />
      <div className="p-6 space-y-6" data-testid="pnl-page">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <KpiCard testId="kpi-pnl-income" label="Ingresos analíticos" value={formatCurrency(data.summary.income)} />
              <KpiCard testId="kpi-pnl-cost" label="Costes analíticos" value={formatCurrency(data.summary.cost)} />
              <KpiCard testId="kpi-pnl-margin" label="Margen" value={formatCurrency(data.summary.margin)} accent={data.summary.margin >= 0 ? "success" : "danger"} />
              <KpiCard testId="kpi-pnl-margin-pct" label="Margen %" value={formatPct(data.summary.margin_pct)} accent={data.summary.margin_pct >= 0 ? "success" : "danger"} />
              <KpiCard testId="kpi-pnl-hours" label="Horas" value={formatHours(data.summary.hours)} />
            </div>

            <Panel
              title="Estructura analítica"
              subtitle="Despliega para navegar empresa → departamento → cuenta. Las filas resaltadas son planes de costes indirectos."
              testId="panel-pnl-tree"
            >
              <div className="overflow-x-auto rounded border border-slate-200">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr className="text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-600">
                      <th className="text-left px-3 py-2 border-b border-slate-200">Plan / Cuenta analítica</th>
                      <th className="text-right px-3 py-2 border-b border-slate-200">Ingresos</th>
                      <th className="text-right px-3 py-2 border-b border-slate-200">Costes</th>
                      <th className="text-right px-3 py-2 border-b border-slate-200">Margen</th>
                      <th className="text-right px-3 py-2 border-b border-slate-200">Margen %</th>
                      <th className="text-right px-3 py-2 border-b border-slate-200">Horas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const { kind, node, depth } = row;
                      if (kind === "plan") {
                        const hasKids =
                          (node.children?.length || 0) > 0 || (node.accounts?.length || 0) > 0;
                        const isOpen = expanded.has(node.id);
                        const Icon = isOpen ? ChevronDown : ChevronRight;
                        const marginColor = node.margin >= 0 ? "text-emerald-600" : "text-red-600";
                        return (
                          <tr
                            key={`p-${node.id}-${idx}`}
                            className={cn(
                              "border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-150",
                              node.is_indirect && "bg-amber-50/40"
                            )}
                            data-testid={`pnl-row-plan-${node.id}`}
                          >
                            <td className="px-3 py-2 text-sm">
                              <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 16}px` }}>
                                {hasKids ? (
                                  <button
                                    onClick={() => toggle(node.id)}
                                    data-testid={`pnl-toggle-${node.id}`}
                                    className="text-slate-400 hover:text-slate-700"
                                  >
                                    <Icon className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <span className="w-4 h-4 inline-block" />
                                )}
                                <span className={cn("font-medium text-slate-800", node.is_indirect && "text-amber-700")}>
                                  {node.name}
                                  {node.is_indirect && <AlertTriangle className="inline w-3 h-3 ml-1 text-amber-500" />}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-sm tabular-nums text-right text-slate-700">{formatCurrency(node.income)}</td>
                            <td className="px-3 py-2 text-sm tabular-nums text-right text-slate-700">{formatCurrency(node.cost)}</td>
                            <td className={cn("px-3 py-2 text-sm tabular-nums text-right font-medium", marginColor)}>
                              {formatCurrency(node.margin)}
                            </td>
                            <td className={cn("px-3 py-2 text-sm tabular-nums text-right", marginColor)}>
                              {formatPct(node.margin_pct)}
                            </td>
                            <td className="px-3 py-2 text-sm tabular-nums text-right text-slate-500">{formatHours(node.hours)}</td>
                          </tr>
                        );
                      }
                      // account leaf
                      const am = node.income - node.cost;
                      const amc = am >= 0 ? "text-emerald-600" : "text-red-600";
                      return (
                        <tr
                          key={`a-${node.id}-${idx}`}
                          className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-150 cursor-pointer group"
                          data-testid={`pnl-row-account-${node.id}`}
                          onClick={() => navigate(`/app/rentabilidad?account=${encodeURIComponent(node.name)}`)}
                          title="Ver rentabilidad de proyectos asociados a esta cuenta"
                        >
                          <td className="px-3 py-2 text-sm">
                            <div style={{ paddingLeft: `${depth * 16 + 16}px` }} className="text-slate-600 flex items-center gap-2">
                              <span className="truncate">{node.name}</span>
                              <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-[#002FA7] transition-colors duration-150" />
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm tabular-nums text-right text-slate-700">{formatCurrency(node.income)}</td>
                          <td className="px-3 py-2 text-sm tabular-nums text-right text-slate-700">{formatCurrency(node.cost)}</td>
                          <td className={cn("px-3 py-2 text-sm tabular-nums text-right font-medium", amc)}>
                            {formatCurrency(am)}
                          </td>
                          <td className="px-3 py-2 text-sm tabular-nums text-right text-slate-400">—</td>
                          <td className="px-3 py-2 text-sm tabular-nums text-right text-slate-500">{formatHours(node.hours)}</td>
                        </tr>
                      );
                    })}
                    {!rows.length && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-400 text-sm">
                          Sin movimientos analíticos en el rango seleccionado
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-xs text-blue-900">
              <strong>Notas:</strong> Los importes provienen de los apuntes analíticos
              (account.analytic.line) dentro del rango. Las filas marcadas como{" "}
              <em className="text-amber-700">"COSTES INDIRECTOS"</em> agrupan los gastos no atribuibles
              a un proyecto específico; en la vista <em>Rentabilidad proyectos</em> se reparten
              proporcionalmente entre los proyectos del mismo departamento según los ingresos directos.
            </div>
          </>
        )}
      </div>
    </>
  );
}
